import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddInstituteModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    instituteName: '',
    email: '',
    phone: '',
    password: '',
    contactPerson: '',
    city: '',
    state: '',
    website: '',
    description: '',
    boothType: 'free'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const boothTypes = [
    { 
      value: 'free', 
      label: 'Free Booth',
      description: 'Basic booth with standard features'
    },
    { 
      value: 'premium', 
      label: 'Premium Booth',
      description: 'Enhanced visibility with priority placement'
    },
    { 
      value: 'sponsored', 
      label: 'Sponsored Booth',
      description: 'Top-tier booth with maximum exposure'
    }
  ];

  const handleChange = (e) => {
    // Handle both event objects (from Input/textarea) and direct values (from Select)
    if (e?.target) {
      const { name, value } = e?.target;
      setFormData(prev => ({ ...prev, [name]: value }));
      setError('');
    }
  };

  const handleBoothTypeChange = (value) => {
    setFormData(prev => ({ ...prev, boothType: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation — check each required field individually for clear feedback
    if (!formData?.instituteName?.trim()) {
      setError('Institute Name is required');
      return;
    }
    if (!formData?.email?.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData?.password?.trim()) {
      setError('Password is required');
      return;
    }

    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      // Reset form
      setFormData({
        instituteName: '',
        email: '',
        phone: '',
        password: '',
        contactPerson: '',
        city: '',
        state: '',
        website: '',
        description: '',
        boothType: 'free'
      });
      onClose();
    } catch (err) {
      // Display user-friendly error messages
      const errorMessage = err?.message || 'Failed to add institute';
      
      // Check for specific error patterns
      if (errorMessage?.includes('already registered') || 
          errorMessage?.includes('duplicate key') || 
          errorMessage?.includes('users_email_key')) {
        setError('This email address is already in use. Please use a different email.');
      } else if (errorMessage?.includes('already in use')) {
        setError('This email address is already in use by an existing institute. Please use a different email.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-heading font-semibold text-foreground">
            Add New Institute
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <Icon name="AlertCircle" size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Institute Name <span className="text-red-500">*</span>
              </label>
              <Input
                name="instituteName"
                value={formData?.instituteName}
                onChange={handleChange}
                placeholder="Enter institute name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                placeholder="institute@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Phone
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleChange}
                placeholder="+91 1234567890"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <Input
                type="password"
                name="password"
                value={formData?.password}
                onChange={handleChange}
                placeholder="Minimum 6 characters"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Contact Person
              </label>
              <Input
                name="contactPerson"
                value={formData?.contactPerson}
                onChange={handleChange}
                placeholder="Contact person name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                City
              </label>
              <Input
                name="city"
                value={formData?.city}
                onChange={handleChange}
                placeholder="City"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                State
              </label>
              <Input
                name="state"
                value={formData?.state}
                onChange={handleChange}
                placeholder="State"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Website
              </label>
              <Input
                type="url"
                name="website"
                value={formData?.website}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Booth Type
              </label>
              <Select
                name="boothType"
                value={formData?.boothType}
                onChange={handleBoothTypeChange}
                options={boothTypes}
              />
              <p className="text-xs text-muted-foreground mt-2">
                {formData?.boothType === 'free' && '• Free: Basic booth with standard features and visibility'}
                {formData?.boothType === 'premium' && '• Premium: Enhanced booth with priority placement and additional features'}
                {formData?.boothType === 'sponsored' && '• Sponsored: Top-tier booth with maximum exposure and premium benefits'}
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData?.description}
                onChange={handleChange}
                placeholder="Brief description about the institute"
                rows={4}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
              iconName={loading ? 'Loader2' : 'Plus'}
            >
              {loading ? 'Adding...' : 'Add Institute'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInstituteModal;